import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FirestoreContextService } from './firestore-context.service';
import { AnalyticsService } from './analytics.service';
import { ScheduledPromotionDoc } from '../models/promotion.model';

/**
 * Scheduled promotions, read straight from the `scheduled-promotions` Firestore collection.
 *
 * Exists for one caller: the media promotion screen a push notification opens. The media URLs are
 * deliberately NOT in the notification payload — FCM data payloads cap at 4KB and a three-item
 * gallery of signed storage URLs plus thumbnails is six 250-300 character strings, on top of the
 * title, body, image, shop name, logo, url and client_id already being sent. Close enough to the
 * ceiling that a long title tips it over, and it fails at send time. So the notification carries
 * the promotion's id in its redirect path and the screen fetches the rest here — the same trade
 * iOS makes with `RegularNotificationData.documentId`. It also means the gallery stays correct if
 * the promotion is edited after the notification went out.
 *
 * Reads are unauthenticated, like `EventsService` — verified against both projects, so the
 * security rules already permit this and no rules deploy is needed.
 *
 * Errors are swallowed to a `null` return, as in `EventsService`, so a dead query renders the
 * screen's not-found state rather than a blank page — and reports itself, so a promotion that was
 * deleted between sending and tapping is distinguishable from a query that broke.
 */
@Injectable({ providedIn: 'root' })
export class PromotionsService {

  constructor(
    private afs: AngularFirestore,
    private fsContext: FirestoreContextService,
    private analytics: AnalyticsService
  ) {}

  /**
   * One promotion by its DOCUMENT KEY.
   *
   * Deliberately `doc(id).get()`, the opposite of `EventsService.getEvent`: the only thing that
   * ever links here is the Cloud Function's redirect path, and that is built from `promoDoc.id` —
   * the document key it just fetched with. A key lookup is also a single read rather than a query,
   * and cannot return the wrong document if the `id` field is ever out of step with the key.
   *
   * Returns null when nothing matches, which the screen renders as its not-found state.
   */
  async getPromotion(promotionId: string): Promise<ScheduledPromotionDoc | null> {
    const id = (promotionId ?? '').trim();
    if (!id) return null;

    try {
      const snapshot = await this.fsContext.run(() => this.afs
        .collection<ScheduledPromotionDoc>('scheduled-promotions')
        .doc(id)
        .get()
      ).toPromise();

      if (!snapshot || !snapshot.exists) return null;
      return snapshot.data() as ScheduledPromotionDoc;
    } catch (error) {
      this.analytics.log('promotion_error', {
        // GA4 truncates parameter values at 100 characters; trim rather than let it happen silently.
        error_message: String((error as any)?.code ?? (error as any)?.message ?? error ?? 'unknown').slice(0, 100)
      });
      return null;
    }
  }
}
